import { AfterViewInit, ChangeDetectorRef, Directive, ElementRef, NgZone, OnDestroy, OnInit, Optional, Self } from '@angular/core';
import { CdkConnectedOverlay, CdkOverlayOrigin, ConnectedPosition, FlexibleConnectedPositionStrategy } from '@angular/cdk/overlay';
import { ScrollDispatcher } from '@angular/cdk/scrolling';
import { NzSelectComponent } from 'ng-zorro-antd/select';
import { NzDatePickerComponent } from 'ng-zorro-antd/date-picker';
import { NzTimePickerComponent } from 'ng-zorro-antd/time-picker';
import { Subscription } from 'rxjs';
import { ControlAccessibilityService } from '../services/control-accessibility.service';

/** Configure CDK through its public positioning API, including narrow viewports.
 * NG-ZORRO 16's default connected overlay does not push oversized menus on screen.
 */
@Directive({ selector: 'nz-select, nz-date-picker, nz-range-picker, nz-time-picker', standalone: true })
export class ControlOverlayDirective implements OnInit, AfterViewInit, OnDestroy {
  private static nextTimeId = 0;
  private readonly timePanelClass = `ur-time-popup-${ControlOverlayDirective.nextTimeId++}`;
  private timePositions: ConnectedPosition[] = [];
  private subscriptions = new Subscription();
  private resize?: ResizeObserver;
  private readonly resizeTargets = new Set<HTMLElement>();
  private frame = 0;
  private connected?: CdkConnectedOverlay;
  private unregisterPopup?: () => void;
  constructor(@Optional() @Self() private select: NzSelectComponent,
    @Optional() @Self() private picker: NzDatePickerComponent,
    @Optional() @Self() private time: NzTimePickerComponent,
    private element: ElementRef<HTMLElement>, private zone: NgZone, private scroll: ScrollDispatcher,
    private controls: ControlAccessibilityService, private cdr: ChangeDetectorRef) {}

  ngOnInit(): void {
    if (this.time) {
      this.time.nzPopupClassName = `${this.time.nzPopupClassName || ''} ${this.timePanelClass}`.trim();
      this.timePositions = [...this.time.overlayPositions];
    }
  }

  ngAfterViewInit(): void {
    this.unregisterPopup = this.controls.registerPopup(this.element.nativeElement, {
      isOpen: () => !!(this.select?.nzOpen || this.picker?.realOpenState || this.time?.nzOpen),
      refresh: this.time ? this.schedule : undefined,
      close: () => {
        this.select?.setOpenState(false);
        this.picker?.close();
        this.time?.close();
      }
    });
    this.configure();
    const open = this.select?.nzOpenChange || this.picker?.nzOnOpenChange || this.time?.nzOpenChange;
    if (open) this.subscriptions.add(open.subscribe(() => this.schedule()));
    this.zone.runOutsideAngular(() => {
      this.resize = new ResizeObserver(() => this.schedule());
      this.observeAncestors();
      window.visualViewport?.addEventListener('resize', this.schedule);
      window.visualViewport?.addEventListener('scroll', this.schedule);
      this.subscriptions.add(this.scroll.scrolled().subscribe(this.schedule));
    });
  }
  private schedule = (): void => {
    if (!this.frame) this.frame = requestAnimationFrame(() => { this.frame = 0; this.configure(); });
  };
  private observeAncestors(): void {
    // Modal content may be constructed before its portal is attached. Discover
    // its final ancestors again on opening, rather than only in AfterViewInit.
    if (!this.resize) return;
    for (let node: HTMLElement | null = this.element.nativeElement; node && node !== document.body; node = node.parentElement) {
      if (!this.resizeTargets.has(node)) { this.resize.observe(node); this.resizeTargets.add(node); }
    }
  }
  private configure(): void {
    this.observeAncestors();
    const overlay = this.select?.cdkConnectedOverlay || this.picker?.cdkConnectedOverlay;
    if (!overlay) {
      // NG-ZORRO 16 exposes time-picker positions/class names, but no overlayRef.
      // Use those public inputs; the unique class identifies only this popup.
      const pane = this.time?.nzOpen ? document.querySelector(`.${this.timePanelClass}`)?.closest<HTMLElement>('.ant-picker-dropdown') : null;
      if (pane && this.time) {
        const positions = this.fitVisualViewport(pane, this.timePositions, this.time.dir === 'rtl', width => {
          pane.style.maxWidth = width === null ? '' : `${width}px`;
        });
        // Reapply even with identical connections: reopening creates a new
        // panel, and keyboard/pan changes can change its constrained height.
        this.time.overlayPositions = [...positions];
        // CDK 16 reapplies changed connections when its origin input changes.
        // Keep the same element, with a fresh public origin wrapper.
        this.time.origin = new CdkOverlayOrigin(this.time.origin.elementRef);
        this.cdr.detectChanges();
      }
      return;
    }
    overlay.push = true;
    // CDK 16 subtracts viewport margins twice on the trailing push edge.
    // CSS bounds menu dimensions; a zero strategy margin preserves alignment
    // at 320px without introducing a second offset from the trigger.
    overlay.viewportMargin = 0;
    if (this.connected !== overlay) {
      this.connected = overlay;
      this.subscriptions.add(overlay.attach.subscribe(() => this.configure()));
    }
    if (overlay.overlayRef?.hasAttached()) {
      const ref = overlay.overlayRef;
      const positions = this.fitVisualViewport(ref.overlayElement, overlay.positions, ref.getDirection() === 'rtl', width => {
        ref.updateSize({ minWidth: width === null ? overlay.minWidth : Math.min(this.element.nativeElement.getBoundingClientRect().width, width), maxWidth: width === null ? '' : width });
      });
      const position = overlay.overlayRef.getConfig().positionStrategy;
      if (position instanceof FlexibleConnectedPositionStrategy) {
        position.withPush(true).withViewportMargin(0)
          .withPositions(positions)
          .withScrollableContainers(this.scroll.getAncestorScrollContainers(this.element));
      }
      overlay.overlayRef.updatePosition();
    }
  }
  private fitVisualViewport(pane: HTMLElement, original: ConnectedPosition[], rtl: boolean, resize: (width: number | null) => void): ConnectedPosition[] {
    const viewport = window.visualViewport;
    const height = viewport?.height || window.innerHeight;
    const width = viewport?.width || document.documentElement.clientWidth;
    const maxWidth = Math.max(0, width - 24);
    pane.style.setProperty('--ur-overlay-width', `${maxWidth}px`);
    const narrowed = !!viewport && (height < document.documentElement.clientHeight - 1 || width < document.documentElement.clientWidth - 1 || viewport.offsetTop > 0 || viewport.offsetLeft > 0);
    if (!narrowed) {
      resize(null);
      pane.style.setProperty('--ur-overlay-height', `${Math.max(0, height - 24)}px`);
      return original;
    }
    // CDK 16 positions against the layout viewport. Restrict its connections to
    // the usable side of the field when keyboards/zoom shrink visualViewport.
    const trigger = this.element.nativeElement.getBoundingClientRect();
    const above = Math.max(0, trigger.top - viewport.offsetTop - 8);
    const below = Math.max(0, viewport.offsetTop + height - trigger.bottom - 8);
    const useAbove = pane.getBoundingClientRect().height > below && above > below;
    resize(maxWidth);
    const menuWidth = Math.min(pane.getBoundingClientRect().width, maxWidth);
    pane.style.setProperty('--ur-overlay-height', `${useAbove ? above : below}px`);
    return original.filter(p => p.originY === (useAbove ? 'top' : 'bottom')).map(p => {
      const originX = p.originX === 'center' ? trigger.left + trigger.width / 2 : (p.originX === 'start') !== rtl ? trigger.left : trigger.right;
      const overlayX = p.overlayX === 'center' ? menuWidth / 2 : (p.overlayX === 'start') !== rtl ? 0 : menuWidth;
      const x = originX - overlayX + (p.offsetX || 0);
      const clamped = Math.max(viewport.offsetLeft, Math.min(x, viewport.offsetLeft + width - menuWidth));
      return { ...p, offsetX: (p.offsetX || 0) + clamped - x };
    });
  }
  ngOnDestroy(): void {
    this.unregisterPopup?.();
    this.subscriptions.unsubscribe(); this.resize?.disconnect(); this.resizeTargets.clear();
    if (this.frame) cancelAnimationFrame(this.frame);
    window.visualViewport?.removeEventListener('resize', this.schedule);
    window.visualViewport?.removeEventListener('scroll', this.schedule);
  }
}
